import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, ChevronLeft, ExternalLink, Loader2,
  MapPin, Stethoscope, Building2, Globe, AlertCircle,
  User, Phone, RefreshCw, Info, Download, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';

const STATES = [
  { value: 'AC', label: 'Acre' }, { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' }, { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' }, { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' }, { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' }, { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' }, { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' }, { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' }, { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' }, { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' }, { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' }, { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' }, { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' }, { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

export default function AdminDoctorsByHealthPlan() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [results, setResults] = useState(null);

  // Form states
  const [selectedOperator, setSelectedOperator] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [specialty, setSpecialty] = useState('');

  // Internal search
  const [searchMode, setSearchMode] = useState('external');
  const [internalResults, setInternalResults] = useState(null);
  const [healthPlans, setHealthPlans] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [selectedHealthPlan, setSelectedHealthPlan] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [importedProviders, setImportedProviders] = useState(new Set());
  const [importingIdx, setImportingIdx] = useState(null);

  useEffect(() => {
    fetchOperators();
    fetchFiltersData();
  }, []);

  const fetchOperators = async () => {
    try {
      const res = await api.get('/admin/operators');
      setOperators(res.data);
    } catch (error) {
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [plansRes, specialtiesRes] = await Promise.all([
        api.get('/admin/health-plans'),
        api.get('/public/specialties')
      ]);
      setHealthPlans(plansRes.data);
      setSpecialties(specialtiesRes.data);
    } catch (error) {
    }
  };

  const handleExternalSearch = async (e) => {
    e?.preventDefault();
    if (!selectedOperator) { toast.error('Selecione uma operadora'); return; }
    if (!state) { toast.error('Selecione um estado'); return; }

    setLoading(true);
    setResults(null);

    try {
      const params = new URLSearchParams({
        operator_id: selectedOperator,
        state: state,
      });
      if (city.trim()) params.set('city', city.trim());
      if (specialty.trim()) params.set('specialty', specialty.trim());

      const response = await api.post(`/admin/scrape-operator-network?${params.toString()}`);
      setResults(response.data);

      if (response.data.success && response.data.total > 0) {
        toast.success(`${response.data.total} profissional(is) encontrado(s)`);
      } else if (response.data.summary) {
        toast.info('Busca realizada. Confira o resumo abaixo.');
      } else {
        toast.warning('Nenhum resultado encontrado. Tente refinar os filtros.');
      }
    } catch (error) {
      toast.error('Erro ao realizar busca');
    } finally {
      setLoading(false);
    }
  };

  const handleInternalSearch = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setInternalResults(null);

    try {
      const params = new URLSearchParams();
      if (selectedHealthPlan) params.set('health_plan_id', selectedHealthPlan);
      if (selectedSpecialty) params.set('specialty_id', selectedSpecialty);
      if (city.trim()) params.set('city', city.trim());
      if (state) params.set('state', state);
      params.set('page', '1');
      params.set('limit', '50');

      const response = await api.get(`/admin/doctors-by-health-plan?${params.toString()}`);
      setInternalResults(response.data);
      toast.success(`${response.data.total} médico(s) encontrado(s)`);
    } catch (error) {
      toast.error('Erro na busca interna');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedOperator('');
    setState('');
    setCity('');
    setSpecialty('');
    setResults(null);
    setInternalResults(null);
    setSelectedHealthPlan('');
    setSelectedSpecialty('');
    setImportedProviders(new Set());
  };

  const handleImportProvider = async (provider, idx) => {
    setImportingIdx(idx);
    try {
      const opName = results?.operator_name || '';
      await api.post('/admin/import-external-provider', {
        ...provider,
        operator_name: opName,
      });
      setImportedProviders(prev => new Set([...prev, idx]));
      toast.success(`${provider.name} importado com sucesso!`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao importar';
      if (error.response?.status === 409) {
        setImportedProviders(prev => new Set([...prev, idx]));
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setImportingIdx(null);
    }
  };

  const handleImportAll = async () => {
    if (!results?.providers?.length) return;
    let imported = 0;
    let skipped = 0;
    for (let i = 0; i < results.providers.length; i++) {
      if (importedProviders.has(i)) { skipped++; continue; }
      try {
        await api.post('/admin/import-external-provider', {
          ...results.providers[i],
          operator_name: results.operator_name || '',
        });
        setImportedProviders(prev => new Set([...prev, i]));
        imported++;
      } catch {
        skipped++;
      }
    }
    toast.success(`${imported} importado(s), ${skipped} já existente(s)`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-doctors-by-health-plan">
      <Navbar />

      <div className="container-app py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin" className="p-2 rounded-lg hover:bg-slate-100" data-testid="back-to-admin">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Buscar Rede Credenciada</h1>
            <p className="text-slate-600 text-sm">Pesquise profissionais credenciados por operadora via busca inteligente</p>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={searchMode === 'external' ? 'default' : 'outline'}
            onClick={() => setSearchMode('external')}
            className={searchMode === 'external' ? 'bg-primary' : ''}
            data-testid="tab-external"
          >
            <Globe className="w-4 h-4 mr-2" />
            Busca Inteligente (Web + IA)
          </Button>
          <Button
            variant={searchMode === 'internal' ? 'default' : 'outline'}
            onClick={() => setSearchMode('internal')}
            className={searchMode === 'internal' ? 'bg-primary' : ''}
            data-testid="tab-internal"
          >
            <Search className="w-4 h-4 mr-2" />
            Base Interna
          </Button>
        </div>

        {/* External Search Form */}
        {searchMode === 'external' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <form onSubmit={handleExternalSearch} className="space-y-6">
              {/* Operator Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Building2 className="w-5 h-5 text-primary" />
                  Selecione a Operadora
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {operators.map((op) => {
                    const isSelected = selectedOperator === op.id;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => setSelectedOperator(op.id)}
                        data-testid={`operator-${op.id}`}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <p className="font-semibold text-slate-900">{op.name}</p>
                        <p className="text-xs text-slate-500 mt-1">Rede credenciada</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Filters */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Estado *
                  </Label>
                  <Select value={state || '__ph__'} onValueChange={(v) => setState(v === '__ph__' ? '' : v)}>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ph__">Selecione...</SelectItem>
                      {STATES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Cidade
                  </Label>
                  <Input
                    type="text"
                    placeholder="Ex: São Paulo"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-primary" />
                    Especialidade
                  </Label>
                  <Input
                    type="text"
                    placeholder="Ex: Cardiologia"
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    data-testid="specialty-input"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button type="submit" className="bg-primary" disabled={loading} data-testid="search-btn">
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  {loading ? 'Pesquisando na web...' : 'Buscar na Web'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClear} data-testid="clear-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Limpar
                </Button>
                {selectedOperator && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const op = operators.find(o => o.id === selectedOperator);
                      if (op) window.open(op.search_url, '_blank', 'noopener,noreferrer');
                    }}
                    data-testid="open-operator-site-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Site da Operadora
                  </Button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Internal Search Form */}
        {searchMode === 'internal' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <form onSubmit={handleInternalSearch} className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label><Building2 className="w-4 h-4 text-primary inline mr-2" />Convênio</Label>
                  <Select value={selectedHealthPlan || '__all__'} onValueChange={(v) => setSelectedHealthPlan(v === '__all__' ? '' : v)}>
                    <SelectTrigger data-testid="health-plan-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {healthPlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label><Stethoscope className="w-4 h-4 text-primary inline mr-2" />Especialidade</Label>
                  <Select value={selectedSpecialty || '__all__'} onValueChange={(v) => setSelectedSpecialty(v === '__all__' ? '' : v)}>
                    <SelectTrigger data-testid="internal-specialty-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas</SelectItem>
                      {specialties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label><MapPin className="w-4 h-4 text-primary inline mr-2" />Cidade</Label>
                  <Input placeholder="Ex: São Paulo" value={city} onChange={(e) => setCity(e.target.value)} data-testid="internal-city-input" />
                </div>
                <div className="space-y-2">
                  <Label><MapPin className="w-4 h-4 text-primary inline mr-2" />Estado</Label>
                  <Select value={state || '__all__'} onValueChange={(v) => setState(v === '__all__' ? '' : v)}>
                    <SelectTrigger data-testid="internal-state-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-primary" disabled={loading} data-testid="internal-search-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Buscar na Base Interna
                </Button>
                <Button type="button" variant="outline" onClick={handleClear}>Limpar</Button>
              </div>
            </form>
          </div>
        )}

        {/* External Search Results */}
        {searchMode === 'external' && results && (
          <div className="space-y-4">
            {/* Summary Card */}
            {results.summary && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">
                      Resumo da Busca - {results.operator_name}
                    </h3>
                    <p className="text-sm text-blue-800 whitespace-pre-line">{results.summary}</p>
                    {results.search_query && (
                      <p className="text-xs text-blue-600 mt-2">
                        Pesquisa: "{results.search_query}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {results.error_message && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-amber-800">{results.error_message}</p>
                    {results.fallback_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => window.open(results.fallback_url, '_blank')}
                        data-testid="fallback-link"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Consultar no site da {results.operator_name}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Provider Results Table */}
            {results.providers && results.providers.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <span className="font-semibold text-slate-900">
                    {results.total} profissional(is) encontrado(s) - {results.operator_name}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">Web + IA</Badge>
                    <Button
                      size="sm"
                      onClick={handleImportAll}
                      disabled={importedProviders.size === results.providers.length}
                      data-testid="import-all-btn"
                      className="bg-primary"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Importar Todos
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="external-results-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-slate-700 text-sm">Profissional</th>
                        <th className="text-left p-3 font-medium text-slate-700 text-sm">Especialidade</th>
                        <th className="text-left p-3 font-medium text-slate-700 text-sm">Estabelecimento</th>
                        <th className="text-left p-3 font-medium text-slate-700 text-sm">Localização</th>
                        <th className="text-left p-3 font-medium text-slate-700 text-sm">Telefone</th>
                        <th className="text-center p-3 font-medium text-slate-700 text-sm">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {results.providers.map((provider, idx) => (
                        <tr key={provider.id || `${provider.name || ''}-${idx}`} className="hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                                <User className="w-4 h-4 text-primary" />
                              </div>
                              <span className="font-medium text-slate-900 text-sm">{provider.name || '-'}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            {provider.specialty ? (
                              <Badge variant="secondary" className="text-xs">{provider.specialty}</Badge>
                            ) : <span className="text-slate-400 text-sm">-</span>}
                          </td>
                          <td className="p-3">
                            {provider.establishment ? (
                              <div className="flex items-center gap-1 text-sm text-slate-700">
                                <Building2 className="w-3 h-3 text-slate-400 shrink-0" />
                                {provider.establishment}
                              </div>
                            ) : <span className="text-slate-400 text-sm">-</span>}
                          </td>
                          <td className="p-3">
                            <div className="text-sm text-slate-700">
                              {provider.neighborhood && <span>{provider.neighborhood}, </span>}
                              {provider.city && <span>{provider.city}</span>}
                              {provider.state && <span>/{provider.state}</span>}
                              {!provider.city && !provider.neighborhood && <span className="text-slate-400">-</span>}
                            </div>
                          </td>
                          <td className="p-3">
                            {provider.phone ? (
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <Phone className="w-3 h-3 shrink-0 text-green-600" />
                                {provider.phone}
                              </div>
                            ) : (
                              <span className="text-xs text-amber-600 italic">Não localizado</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {importedProviders.has(idx) ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium" data-testid={`imported-${idx}`}>
                                <Check className="w-4 h-4" />
                                Importado
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleImportProvider(provider, idx)}
                                disabled={importingIdx === idx}
                                data-testid={`import-btn-${idx}`}
                                className="text-xs"
                              >
                                {importingIdx === idx ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <><Download className="w-3.5 h-3.5 mr-1" />Importar</>
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Operator Direct Links */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-3 text-sm">Links Diretos das Operadoras</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
                {operators.map((op) => (
                  <a
                    key={op.id}
                    href={op.search_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-primary/30 hover:bg-primary/5 transition-all text-sm"
                    data-testid={`link-${op.id}`}
                  >
                    <Globe className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="font-medium text-slate-800">{op.name}</span>
                    <ExternalLink className="w-3 h-3 text-slate-400 ml-auto shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Internal Results */}
        {searchMode === 'internal' && internalResults && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <span className="font-semibold text-slate-900">{internalResults.total} médico(s) na base interna</span>
            </div>
            {internalResults.doctors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="internal-results-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-700 text-sm">Médico</th>
                      <th className="text-left p-3 font-medium text-slate-700 text-sm">CRM</th>
                      <th className="text-left p-3 font-medium text-slate-700 text-sm">Especialidade</th>
                      <th className="text-left p-3 font-medium text-slate-700 text-sm">Cidade</th>
                      <th className="text-left p-3 font-medium text-slate-700 text-sm">Convênios</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {internalResults.doctors.map((doc) => (
                      <tr key={doc.id} className="hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                            <span className="font-medium text-slate-900 text-sm">{doc.full_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-slate-700">{doc.crm}</td>
                        <td className="p-3"><Badge variant="secondary" className="text-xs">{doc.specialty_name || 'N/A'}</Badge></td>
                        <td className="p-3 text-sm">{doc.city || doc.clinic_city || '-'}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {(doc.health_plans_names || []).slice(0, 3).map((plan, i) => (
                              <Badge key={plan} className="bg-green-100 text-green-700 text-[10px]">{plan}</Badge>
                            ))}
                            {(doc.health_plans_names || []).length > 3 && (
                              <Badge variant="secondary" className="text-[10px]">+{doc.health_plans_names.length - 3}</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhum médico encontrado</p>
              </div>
            )}
          </div>
        )}

        {/* Empty States */}
        {searchMode === 'external' && !results && !loading && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Busca Inteligente de Rede Credenciada</h3>
            <p className="text-slate-500 max-w-lg mx-auto">
              Selecione uma operadora, estado e cidade. O sistema pesquisará na web e usará
              inteligência artificial para organizar os profissionais encontrados.
            </p>
          </div>
        )}

        {searchMode === 'internal' && !internalResults && !loading && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Busca na Base Interna</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Pesquise médicos cadastrados no BuscaDoutor por convênio, especialidade e localização.
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchMode === 'external' ? 'Pesquisando na web...' : 'Buscando...'}
            </h3>
            {searchMode === 'external' && (
              <p className="text-slate-500 text-sm">
                Buscando informações e organizando com IA. Isso pode levar alguns segundos.
              </p>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
